import React, { useState, useRef, useEffect } from 'react';
import './ConfirmationModal.css';
import '../SignInModal/SignInModal.css';
import { emptyCode, fillCodeDigits, isCodeComplete, resendCodeErrorMessage } from '../accountFlow';

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  username,
  errorMessage,
  loading,
  loadingTitle = 'Setting Up Profile',
  resendCode,
  onRetry,
  retryLabel = 'Try again',
  title = 'Confirm Email',
  notice,
}) => {
  const [digits, setDigits] = useState(emptyCode);
  const [submitting, setSubmitting] = useState(false);
  const [resendState, setResendState] = useState({ status: 'idle', message: '' });
  const submittingRef = useRef(false);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setDigits(emptyCode());
    setResendState({ status: 'idle', message: '' });
    submittingRef.current = false;
    setSubmitting(false);
    const timer = setTimeout(() => inputsRef.current[0]?.focus(), 0);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const submit = async (code) => {
    if (submittingRef.current || loading) return;
    submittingRef.current = true;
    setSubmitting(true);
    let ok = false;
    try {
      const result = await onConfirm(code.join(''));
      ok = !!(result && result.success);
    } catch (e) {
      ok = false;
    }
    submittingRef.current = false;
    setSubmitting(false);
    if (!ok) {
      setDigits(emptyCode());
      setTimeout(() => inputsRef.current[0]?.focus(), 0);
    }
  };

  const applyInput = (index, raw) => {
    if (submittingRef.current || loading) return;
    const { digits: next, focus } = fillCodeDigits(digits, index, raw);
    setDigits(next);
    inputsRef.current[focus]?.focus();
    if (isCodeComplete(next)) submit(next);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      e.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      setDigits(next);
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    applyInput(index, e.clipboardData.getData('text'));
  };

  const handleResend = async () => {
    if (!resendCode || resendState.status === 'sending' || submitting) return;
    setResendState({ status: 'sending', message: '' });
    try {
      await resendCode();
      setResendState({ status: 'sent', message: 'We sent a new code. Check your email.' });
    } catch (error) {
      setResendState({ status: 'error', message: resendCodeErrorMessage(error) });
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="confirmation-modal-overlay">
      <div className="confirmation-modal" role="dialog" aria-modal="true" aria-labelledby="confirmation-modal-title">
        {onClose && !loading && (
          <button type="button" className="spiggl-sign-in-close-button" onClick={onClose} aria-label="Close">×</button>
        )}
        <div className="confirmation-modal-header"></div>
        <div className="confirmation-modal-body">
          {loading ? (
            <>
              <h2 id="confirmation-modal-title">{loadingTitle}</h2>
              <div className="confirmation-loading">
                <div className="confirmation-spinner"></div>
              </div>
            </>
          ) : onRetry ? (
            <>
              <h2 id="confirmation-modal-title">{title}</h2>
              {errorMessage && <div className="error-message" role="alert">{errorMessage}</div>}
              <button type="button" className="spiggl-sign-in-button" onClick={onRetry}>{retryLabel}</button>
            </>
          ) : (
            <>
              <h2 id="confirmation-modal-title">{title}</h2>
              {username && <p>Enter the 6-digit code we sent to {username}</p>}
              {notice && <p>{notice}</p>}
              <div className="code-inputs">
                {digits.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    aria-label={`Digit ${index + 1} of 6`}
                    value={digit}
                    onChange={(e) => applyInput(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={(e) => handlePaste(e, index)}
                    onFocus={(e) => e.target.select()}
                    ref={(el) => (inputsRef.current[index] = el)}
                    disabled={submitting}
                  />
                ))}
              </div>
              {errorMessage && !submitting && <div className="error-message" role="alert">{errorMessage}</div>}
              {submitting && <div className="loading-message">Verifying code...</div>}
              {resendState.message && (
                <div className={resendState.status === 'error' ? 'error-message' : 'loading-message'} role="status">
                  {resendState.message}
                </div>
              )}
              {resendCode && (
                <div className="confirmation-modal-text-message">
                  No email? Check spam or
                  <span
                    className="resend-link"
                    role="button"
                    tabIndex={0}
                    onClick={handleResend}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleResend(); } }}
                  >
                    {resendState.status === 'sending' ? 'sending...' : 'resend code'}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
